import { DP_FILLER, MA_HONORIFIC, dpNameWord } from "./enum-slot.js";
import { isFirstPersonPronoun } from "./member-slot.js";
import { sameListedWord, sameWord, wordsOf, type Word } from "./vi-words.js";

/**
 * 🔴 UX-SMALL-WORDING (V-5 · @UC0161 @UC0320) — chữ của ô NGƯỜI có NÊU một người không (một tên, hay một lời gọi người: "anh", "chị", "mình").
 *
 * Câu hỏi lại *"Mình chưa rõ bạn nói ai."* (`aiClarifyUnmatchedMemberText`) chỉ đúng khi người dùng ĐÃ nói một người mà app không khớp được. Đo B0 (LLM
 * thật, 16/10): Phiếu gạch ô người bằng chữ KHÔNG nói ai — cả câu *"bao da chuyen tien"* (ô `be` của PAY-REPORT) · *"xac nhan da nhan tien"* · chữ chung
 * *"thanh vien"* (ô `nguoi` của PAY-CONFIRM-DECIDE) ⇒ resolver `unmatched` ⇒ app trách người dùng "chưa rõ bạn nói ai" khi họ không nói ai. Hàm này chỉ
 * đổi CÂU (tầng api `clarifyTurn`): lý do `unmatched` vẫn giữ nguyên ở ô (không lớp nào tự điền từ nó — #1029), danh sách vẫn là danh sách resolver.
 *
 * Luật: còn ÍT NHẤT một chữ có thể là (một phần) TÊN (`dpNameWord` — cùng vị-từ chữ-tên của chốt PCD-HARDEN, một nguồn: ngoài ngữ pháp khuôn tiền về ·
 * đại từ/chữ chung · thời gian · phủ định · số · `DP_FILLER`) hoặc một lời gọi người (xưng hô · đại từ ngôi 1), SAU KHI bỏ chữ của VIỆC (tập đóng dưới).
 * Chữ lạ bị coi là tên ⇒ câu cũ "chưa rõ bạn nói ai" (đúng như trước) — chiều sai chỉ là câu chữ, không bao giờ một người.
 */
const ACTION_PHRASES: readonly (readonly Word[])[] = ["xác nhận", "từ chối", "thành viên", "hội viên", "phụ huynh", "mọi người", "đóng hộ", "rút lại"].map(wordsOf);
/** Chữ ĐƠN của việc/chữ chung không bao giờ là tên — tập ĐÓNG, chỉ thêm khi đo được. */
const ACTION_WORDS: readonly Word[] = wordsOf("duyệt ghi nhắc member mem các mấy những");

const isFiller = (x: Word): boolean => DP_FILLER.some((f) => sameWord(x, f));

export function memberTextNamesSomeone(text: string): boolean {
  const w = wordsOf(text);
  // review V-5 M1: MỘT chữ gõ không dấu đứng riêng ("bao") — có thể là tên ("Bảo") trùng mặt chữ việc ("báo") ⇒ coi là nêu người (câu cũ), trừ chữ việc thuần.
  if (w.length === 1 && w[0]!.plain && !ACTION_WORDS.some((a) => sameListedWord(w[0]!, a))) return true;
  const inPhrase = new Set<number>();
  for (const p of ACTION_PHRASES) {
    for (let i = 0; i + p.length <= w.length; i += 1) {
      if (p.every((x, j) => sameListedWord(w[i + j]!, x))) for (let j = 0; j < p.length; j += 1) inPhrase.add(i + j);
    }
  }
  return w.some((x, i) => {
    if (inPhrase.has(i) || ACTION_WORDS.some((a) => sameListedWord(x, a))) return false;
    return dpNameWord(x, isFiller) || MA_HONORIFIC.some((h) => sameWord(x, h)) || isFirstPersonPronoun(x.accented);
  });
}
